import { useAuth0, User } from '@auth0/auth0-react';
import { useCallback, useMemo } from 'react';

/**
 * Namespace utilized for custom claims within Auth0 tokens for 99minutos.
 */
const CLAIMS_NAMESPACE = 'https://oauth.99minutos.com/';

/**
 * Return type definition for the useAuth hook.
 */
export interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | undefined;
  login: () => Promise<void>;
  logout: () => void;
  getClaim: <ExpectedType = unknown>(claimName: string) => ExpectedType | undefined;
  hasPermission: (permissionToVerify: string) => boolean;
}

/**
 * Custom hook to abstract Auth0 authentication logic.
 *
 * What it does:
 * Provides authentication state, user information, and methods for login/logout.
 * It also includes helper methods to retrieve claims and check permissions safely.
 *
 * What it is used for:
 * Used by components to manage and react to the user's authentication status and
 * authorization level within the application.
 *
 * Preconditions:
 * - Must be used within a component tree wrapped by Auth0Provider.
 *
 * @returns Object containing authentication state and methods
 */
export function useAuth(): UseAuthReturn {
  const { isAuthenticated, isLoading, user, loginWithRedirect, logout: auth0Logout } = useAuth0();

  /**
   * Initiates the login flow by redirecting to the Auth0 Universal Login page.
   */
  const login = useCallback(async (): Promise<void> => {
    await loginWithRedirect();
  }, [loginWithRedirect]);

  /**
   * Clears the local session and redirects the user to Auth0 to clear the SSO session,
   * then returns the user to the application's root origin.
   */
  const logout = useCallback((): void => {
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout]);

  /**
   * Extracts a specific claim from the user's token payload.
   *
   * What it does:
   * First attempts to find the claim using the 99minutos namespace. If not found,
   * falls back to looking for the claim directly on the user object.
   *
   * @param claimName - The string identifier of the claim to retrieve.
   * @returns The value of the claim cast to ExpectedType, or undefined if not found or user is not logged in.
   */
  const getClaim = useCallback(
    <ExpectedType = unknown>(claimName: string): ExpectedType | undefined => {
      if (!user) {
        return undefined;
      }

      const namespacedClaim = `${CLAIMS_NAMESPACE}${claimName}`;

      if (namespacedClaim in user) {
        return user[namespacedClaim] as ExpectedType;
      }

      return user[claimName] as ExpectedType;
    },
    [user],
  );

  /**
   * Extracts the user's permissions array from the claims.
   * Memoized to prevent unnecessary re-evaluations on re-renders when the user object hasn't changed.
   */
  const permissionsArray = useMemo<string[]>(() => {
    const extractedPermissions = getClaim<string[]>('permissions');

    if (Array.isArray(extractedPermissions)) {
      return extractedPermissions;
    }

    return [];
  }, [getClaim]);

  /**
   * Verifies if the authenticated user possesses a specific permission.
   *
   * @param permissionToVerify - The exact string identifier of the permission to check.
   * @returns True if the user has the permission, false otherwise.
   */
  const hasPermission = useCallback(
    (permissionToVerify: string): boolean => {
      return permissionsArray.includes(permissionToVerify);
    },
    [permissionsArray],
  );

  return {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    getClaim,
    hasPermission,
  };
}
